import type { ReactNode } from 'react'
import { Button } from '@systemeio/components'
import { XCircleIcon } from '@systemeio/icons'
import { Trans, useTranslation } from 'next-i18next/pages'
import { useRouter } from 'next/router'
import { AccessDenied } from 'shared/errors/access-denied'
import { UnauthorizedResponse } from 'shared/errors/unauthorized-response'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { useJoinCommunity } from '../api/use-join-community'
import { useCommunityWithTopics } from '../hooks/use-community-with-topics'
import useIsUserMember from '../hooks/use-is-user-member'
import useMembersCount from '../hooks/use-members-count'

function StickyJoin({ closeJoin }: { closeJoin: () => void }): ReactNode {
  const router = useRouter()
  const { t } = useTranslation()
  const { communityPath } = router.query
  const { membersCount } = useMembersCount()
  const { mutate } = useIsUserMember()
  const { user } = useUser()

  const { data: community, isValidating: isCommunityLoading } = useCommunityWithTopics()
  const joinCommunityMutation = useJoinCommunity()

  async function join(): Promise<void> {
    if (!communityPath) return
    try {
      if (isAuthorizedUser(user)) {
        await joinCommunityMutation.mutateAsync(communityPath)
        await mutate()
        closeJoin()
      } else {
        // the public community has an own login form, and we need to make custom redirection
        await router.replace(`/community/${communityPath}/join`)
      }
    } catch (error) {
      if (error instanceof UnauthorizedResponse) {
        await router.replace(`/community/${communityPath}/join`)
      } else if (error instanceof AccessDenied) {
        await router.replace(`/community/403`)
      }
    }
  }

  if (community && community.private) {
    return <div />
  }

  if (isCommunityLoading || !community) {
    return (
      <div className="sticky top-[69px] bg-bg-primary shadow-1">
        <div className="mt-5 size-6 animate-pulse rounded-xs bg-bg-tertiary" />
      </div>
    )
  }

  return (
    <div className="bg-bg-primary p-5 shadow-1 max-lg:mb-6 lg:p-0">
      <div className="mx-auto flex max-w-8xl items-center justify-between px-0 sm:px-2 md:px-5 lg:py-3.5">
        <div>
          <div className="text-lg font-semibold text-text-primary lg:text-xl">
            <Trans
              i18nKey="join_form.title"
              values={{ name: community.name }}
              components={{
                strong: <strong />,
                href: <a href={`/community/${communityPath}`} className="underline" />,
              }}
            />
          </div>
          <div className="text-text-secondary">
            {t('sticky-join.members_count', { count: membersCount })}
          </div>
        </div>
        <div className="flex items-center gap-5">
          <Button variant="primary" onClick={join}>
            {t('join_form.button.join.title')}
          </Button>
          <button onClick={closeJoin}>
            <XCircleIcon className="size-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default StickyJoin
